import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, FileText, MoreVertical, Pencil, Trash2, ArrowUpRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useQuotes, useDeleteQuote } from '@/hooks/useSales';
import {
  QUOTE_STATUS_LABELS, QUOTE_STATUSES,
  type Quote, type QuoteStatus,
} from '@/types/sales';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, SectionHeader } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { QuoteStatusBadge } from '@/components/quotes/StatusBadges';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { formatCurrency } from '@/lib/utils';

export function QuotesListPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'quotes', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | QuoteStatus>('');

  const { data, isLoading, error } = useQuotes();
  const deleteQuote = useDeleteQuote();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Quote | null>(null);
  const [deleting, setDeleting] = useState<Quote | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((q) => {
      if (status && q.status !== status) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const hay = `${q.number} ${q.clientName ?? ''} ${q.projectName ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, status]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, open: 0, accepted: 0, value: 0 };
    return {
      total: data.length,
      open: data.filter((q) => q.status === 'sent' || q.status === 'draft').length,
      accepted: data.filter((q) => q.status === 'accepted' || q.status === 'converted').length,
      value: data
        .filter((q) => q.status === 'sent' || q.status === 'accepted')
        .reduce((s, q) => s + (q.totals?.total ?? 0), 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load quotes"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteQuote.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.number}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      <SectionHeader
        kicker={`Estimating · ${data?.length ?? 0} quotes`}
        title="Quotes & Estimates."
        description="Itemized estimates with margin tracking. One-click conversion to work orders once accepted."
        action={canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Quote
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"        value={String(stats.total)} />
        <Stat label="Open"         value={String(stats.open)} />
        <Stat label="Accepted"     value={String(stats.accepted)} />
        <Stat label="Open Value"   value={stats.value ? formatCurrency(stats.value) : '—'} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by number, client, or project…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as QuoteStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              ...QUOTE_STATUSES.map((s) => ({ value: s, label: QUOTE_STATUS_LABELS[s] })),
            ]}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileText}
            title={search || status ? 'No quotes match your filters' : 'No quotes yet'}
            description={search || status ? 'Try clearing some filters.' : 'Build your first quote to get started.'}
            action={canWrite && !search && !status && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                New Quote
              </Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Quote</th>
                <th className="kicker px-6 py-3 font-normal">Client / Project</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal">Issued</th>
                <th className="kicker px-6 py-3 font-normal">Expires</th>
                <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors group">
                  <td className="px-6 py-4">
                    <Link to={`/quotes/${q.id}`} className="font-mono text-brass-300 hover:text-brass-200">
                      {q.number}
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-text">{q.clientName ?? <span className="text-text-dim">Unknown</span>}</div>
                    {q.projectName && <div className="text-xs text-text-dim truncate max-w-xs">{q.projectName}</div>}
                  </td>
                  <td className="px-6 py-4"><QuoteStatusBadge status={q.status} /></td>
                  <td className="px-6 py-4 text-text-muted tabular text-xs">
                    {q.issueDate ? new Date(q.issueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td className="px-6 py-4 text-text-muted tabular text-xs">
                    {q.expirationDate ? new Date(q.expirationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td className="px-6 py-4 text-right tabular text-text">
                    {q.totals?.total ? formatCurrency(q.totals.total) : '—'}
                  </td>
                  <td className="px-6 py-4 text-right relative">
                    {canWrite && (
                      <>
                        <button
                          onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === q.id ? null : q.id); }}
                          className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                          aria-label="Actions"
                        >
                          <MoreVertical size={15} />
                        </button>
                        {openMenu === q.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                            <div className="absolute right-4 top-12 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                              <Link to={`/quotes/${q.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover">
                                <ArrowUpRight size={13} /> Open
                              </Link>
                              <button onClick={() => { setEditing(q); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                <Pencil size={13} /> Edit
                              </button>
                              <div className="my-1 border-t border-border" />
                              <button onClick={() => { setDeleting(q); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                <Trash2 size={13} /> Delete
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <QuoteFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <QuoteFormModal open={!!editing} onClose={() => setEditing(null)} quote={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={deleteQuote.isPending}
        destructive
        title={`Delete ${deleting?.number}?`}
        confirmLabel="Delete quote"
        description="This permanently removes the quote. Cannot be undone."
      />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : 'text-text'}`}>{value}</div>
    </div>
  );
}
